/**
 * Generador de scripts de Balanceador
 * Genera scripts NTH y PCC para balanceo de carga
 * Migrado de Python (backend/server.py) a JavaScript
 */

import { 
  getInterfaceSetCommand, 
  getBaseIp, 
  getNetwork,
  getLanGatewayIp,
  getRoutingMarkLetter,
  getFormattedDate
} from '../utils/routeros.js';

import { validateBalanceadorConfig } from '../utils/validators.js';

/**
 * Genera script de balanceo NTH
 * @param {object} config - Configuración completa
 * @returns {string} Script generado
 */
export function generateBalanceadorNth(config) {
  // Validar configuración
  validateBalanceadorConfig(config);
  
  const numWans = config.wans.length;
  const lanCidr = config.router.subnetLan;
  const lanNetwork = getNetwork(lanCidr);
  const baseIp = getBaseIp(lanCidr);
  const lanIp = `${baseIp}.1`;
  const dnsServers = config.router.dnsServers.join(',');
  const version = config.routerosVersion;
  
  let script = `# MikroTik Load Balancing Script - NTH
# Generated: ${getFormattedDate()}
# Configuration: ${numWans} WANs - NTH Method
# RouterOS Version: ${version.toUpperCase()}

/system identity
set name="${config.router.nombre}"

/system clock
set time-zone-name=${config.router.zonaHoraria}

/interface bridge
add name=LAN

`;
  
  // Configure WAN interfaces
  script += "/interface ethernet\n";
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    script += `${getInterfaceSetCommand(version, wan.puerto, `WAN${i}`)}\n`;
  });
  script += "\n";
  
  // IP Pool for DHCP
  const dhcpStart = `${baseIp}.2`;
  const dhcpEnd = `${baseIp}.254`;
  script += `/ip pool
add name=dhcp_pool ranges=${dhcpStart}-${dhcpEnd}

/ip dhcp-server
add address-pool=dhcp_pool disabled=no interface=LAN name=dhcp1

/ip address
add address=${lanNetwork}/24 interface=LAN network=${lanNetwork}
`;
  
  // WAN IP addresses
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    if (wan.tipo === "fijo") {
      const wanNetmaskBits = wan.netmask.split('.').reduce((acc, octet) => {
        return acc + parseInt(octet).toString(2).split('1').length - 1;
      }, 0);
      const wanNetwork = wan.ip.split('.').slice(0, 3).join('.') + '.0';
      script += `add address=${wan.ip}/${wanNetmaskBits} interface=WAN${i} network=${wanNetwork}\n`;
    }
  });
  
  script += `
/ip dhcp-server network
add address=${lanCidr} dns-server=${dnsServers} gateway=${lanIp}

`;
  
  // DHCP clients for dynamic WANs
  const dhcpWans = config.wans
    .map((wan, index) => ({ wan, i: index + 1 }))
    .filter(({ wan }) => wan.tipo === "dhcp");
  
  if (dhcpWans.length > 0) {
    script += "/ip dhcp-client\n";
    dhcpWans.forEach(({ i }) => {
      script += `add default-route-distance=0 dhcp-options=hostname,clientid interface=WAN${i}\n`;
    });
    script += "\n";
  }
  
  // Firewall Mangle - NTH
  script += `/ip firewall mangle
`;
  for (let i = 1; i <= numWans; i++) {
    script += `add action=mark-connection chain=prerouting connection-state=new \\
    in-interface=LAN new-connection-mark=Mark${i} nth=${numWans},${i-1}
add action=mark-routing chain=prerouting connection-mark=Mark${i} \\
    in-interface=LAN new-routing-mark=AW${getRoutingMarkLetter(i)} passthrough=no
`;
  }
  
  // NAT
  script += "\n/ip firewall nat\n";
  for (let i = 1; i <= numWans; i++) {
    script += `add action=masquerade chain=srcnat connection-mark=Mark${i} out-interface=WAN${i}\n`;
  }
  
  // Routes
  script += "\n/ip route\n";
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    const routingMark = `AW${getRoutingMarkLetter(i)}`;
    const gateway = wan.tipo === "fijo" ? wan.gateway : `${baseIp}.254`;
    script += `add distance=1 gateway=${gateway} routing-mark=${routingMark} scope=255\n`;
  });
  
  // Default routes with gateway check
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    const gateway = wan.tipo === "fijo" ? wan.gateway : `${baseIp}.254`;
    script += `add check-gateway=ping distance=${i} gateway=${gateway}\n`;
  });
  
  script += `
/system routerboard settings
set protected-routerboot=disabled
`;
  
  return script;
}

/**
 * Genera script de balanceo PCC
 * @param {object} config - Configuración completa
 * @returns {string} Script generado
 */
export function generateBalanceadorPcc(config) {
  // Validar configuración
  validateBalanceadorConfig(config);
  
  const numWans = config.wans.length;
  const lanCidr = config.router.subnetLan;
  const lanNetwork = getNetwork(lanCidr);
  const baseIp = getBaseIp(lanCidr);
  const lanIp = `${baseIp}.1`;
  const dnsServers = config.router.dnsServers.join(',');
  const version = config.routerosVersion;
  
  let script = `# MikroTik Load Balancing Script - PCC
# Generated: ${getFormattedDate()}
# Configuration: ${numWans} WANs - PCC Method
# RouterOS Version: ${version.toUpperCase()}

/system identity
set name="${config.router.nombre}"

/system clock
set time-zone-name=${config.router.zonaHoraria}

/interface bridge
add name=LAN

`;
  
  // Configure WAN interfaces
  script += "/interface ethernet\n";
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    script += `${getInterfaceSetCommand(version, wan.puerto, `WAN${i}`)}\n`;
  });
  script += "\n";
  
  // IP Pool for DHCP
  const dhcpStart = `${baseIp}.5`;
  const dhcpEnd = `${baseIp}.250`;
  script += `/ip pool
add name=pool1LAN ranges=${dhcpStart}-${dhcpEnd}

/ip dhcp-server
add address-pool=pool1LAN disabled=no interface=LAN name=ServidorDhcpLAN

/ip address
add address=${lanNetwork}/24 interface=LAN network=${lanNetwork}
`;
  
  // WAN IP addresses
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    if (wan.tipo === "fijo") {
      const wanNetwork = wan.ip.split('.').slice(0, 3).join('.') + '.0';
      script += `add address=${wan.ip}/24 interface=WAN${i} network=${wanNetwork}\n`;
    }
  });
  
  script += `
/ip dhcp-server network
add address=${lanCidr} dns-server=${dnsServers} gateway=${lanIp}

`;
  
  // DHCP clients for dynamic WANs
  const dhcpWans = config.wans
    .map((wan, index) => ({ wan, i: index + 1 }))
    .filter(({ wan }) => wan.tipo === "dhcp");
  
  if (dhcpWans.length > 0) {
    script += "/ip dhcp-client\n";
    dhcpWans.forEach(({ i }) => {
      script += `add default-route-distance=0 dhcp-options=hostname,clientid interface=WAN${i}\n`;
    });
    script += "\n";
  }
  
  // Firewall Mangle - PCC
  script += `/ip firewall mangle
`;
  
  // Mark connections from WANs
  for (let i = 1; i <= numWans; i++) {
    script += `add action=mark-connection chain=prerouting connection-mark=no-mark \\
    in-interface=WAN${i} new-connection-mark=Wan${i}_Conn
`;
  }
  
  // Mark new connections with PCC
  for (let i = 0; i < numWans; i++) {
    script += `add action=mark-connection chain=prerouting connection-mark=no-mark \\
    dst-address-type=!local in-interface=LAN new-connection-mark=Wan${i+1}_Conn \\
    per-connection-classifier=both-addresses:${numWans}/${i}
`;
  }
  
  // Mark routing
  for (let i = 1; i <= numWans; i++) {
    script += `add action=mark-routing chain=prerouting connection-mark=Wan${i}_Conn \\
    in-interface=LAN new-routing-mark=To_Wan${i}
`;
  }
  
  // Mark routing for output
  for (let i = 1; i <= numWans; i++) {
    script += `add action=mark-routing chain=output connection-mark=Wan${i}_Conn \\
    new-routing-mark=To_Wan${i}
`;
  }
  
  // NAT
  script += "\n/ip firewall nat\n";
  for (let i = 1; i <= numWans; i++) {
    script += `add action=masquerade chain=srcnat out-interface=WAN${i}\n`;
  }
  
  // Routes
  script += "\n/ip route\n";
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    const gateway = wan.tipo === "fijo" ? wan.gateway : `${baseIp}.254`;
    script += `add check-gateway=ping distance=1 gateway=${gateway} routing-mark=To_Wan${i}\n`;
  });
  
  // Default routes
  config.wans.forEach((wan, index) => {
    const i = index + 1;
    const gateway = wan.tipo === "fijo" ? wan.gateway : `${baseIp}.254`;
    script += `add check-gateway=ping distance=${i} gateway=${gateway}\n`;
  });
  
  script += `
/system routerboard settings
set protected-routerboot=disabled
`;
  
  return script;
}

/**
 * Genera script de balanceador (selector de tipo)
 * @param {object} config - Configuración completa
 * @returns {string} Script generado
 */
export function generateBalanceadorScript(config) {
  if (config.tipo === 'nth') {
    return generateBalanceadorNth(config);
  } else if (config.tipo === 'pcc') {
    return generateBalanceadorPcc(config);
  } else {
    throw new Error('Tipo de balanceo inválido. Debe ser "nth" o "pcc"');
  }
}
